import { useEffect, useRef } from "react";

/**
 * Abstract flowing mesh / waveform terrain for the Visual Data panel.
 *
 * Base: the original layered horizontal wave-stream lines (stacked sine
 * lines with perspective depth) — preserved exactly.
 *
 * Added: occasional "ingestion surges" — localized, gaussian-shaped spikes
 * that rise (rarely dip) within the existing stream lines, skewed slightly
 * across stacked lines so they read as a controlled data surge. They appear
 * at irregular pseudo-random intervals, fade in/out smoothly, and are mostly
 * graphite / silver-grey / soft-white with a few muted antique-gold highlights.
 * The stream stays calm between surges — stable flow with intermittent
 * ingestion pressure.
 */
export const VisualDataCanvas = () => {
  const ref = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const GOLD = "203,174,120";
    const SILVER = "215,220,227";

    let w = 0;
    let h = 0;
    let t = 0;
    let last = performance.now();
    let running = true;
    const STEP = 7;
    const LINES = 22;

    const rand = (a, b) => a + Math.random() * (b - a);

    // ---- surge (ingestion spike) pool ------------------------------------
    const surges = [];
    let surgeTimer = rand(0.8, 2.0);

    const spawnSurge = () => {
      if (surges.length > 4) return;
      surges.push({
        x0: rand(w * 0.12, w * 0.88),
        sigma: rand(w * 0.045, w * 0.1), // horizontal width of the surge
        skew: rand(-3.4, 3.4), // px shift per line index -> diagonal ridge
        amp: rand(0.55, 1.05), // strength multiplier over the line amplitude
        dir: Math.random() < 0.82 ? -1 : 1, // mostly rise (up), rarely dip
        born: t,
        life: rand(1.7, 3.1),
        gold: Math.random() < 0.26, // a few muted antique-gold highlights
      });
    };

    const surgeEnv = (s) => {
      const phase = (t - s.born) / s.life;
      if (phase <= 0 || phase >= 1) return 0;
      // smooth rise + fade, with a soft hold near the peak
      return Math.pow(Math.sin(Math.PI * phase), 0.85);
    };

    // accumulated vertical offset from all active surges at (x, line)
    const surgeOffset = (x, li, prog, amp) => {
      let off = 0;
      for (let k = 0; k < surges.length; k++) {
        const s = surges[k];
        const env = s._env;
        if (env <= 0.001) continue;
        const sx = s.x0 + (li - LINES / 2) * s.skew;
        const dx = x - sx;
        const g = Math.exp(-(dx * dx) / (2 * s.sigma * s.sigma));
        if (g < 0.004) continue;
        off += s.dir * s.amp * amp * (0.4 + prog * 0.9) * env * g;
      }
      return off;
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // refresh per-surge envelope once per frame
      for (let k = surges.length - 1; k >= 0; k--) {
        surges[k]._env = surgeEnv(surges[k]);
        if ((t - surges[k].born) / surges[k].life >= 1) surges.splice(k, 1);
      }

      // base layered stream lines (silver) + surge offsets
      for (let li = 0; li < LINES; li++) {
        const prog = li / (LINES - 1);
        const baseY = h * 0.16 + prog * h * 0.68;
        const amp = 6 + prog * 30;
        ctx.beginPath();
        for (let x = 0; x <= w; x += STEP) {
          const y =
            baseY +
            Math.sin(x * 0.011 + t + li * 0.5) * amp * 0.45 +
            Math.sin(x * 0.031 - t * 0.7 + li) * amp * 0.3 +
            surgeOffset(x, li, prog, amp);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        const a = 0.04 + prog * 0.16;
        ctx.strokeStyle = "rgba(" + SILVER + "," + a.toFixed(3) + ")";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // muted antique-gold overlay only across active gold surges
      for (let k = 0; k < surges.length; k++) {
        const s = surges[k];
        if (!s.gold || s._env <= 0.02) continue;
        for (let li = 0; li < LINES; li++) {
          const prog = li / (LINES - 1);
          const baseY = h * 0.16 + prog * h * 0.68;
          const amp = 6 + prog * 30;
          const sx = s.x0 + (li - LINES / 2) * s.skew;
          const x0 = Math.max(0, sx - s.sigma * 2.6);
          const x1 = Math.min(w, sx + s.sigma * 2.6);
          if (x1 - x0 < STEP) continue;
          ctx.beginPath();
          let first = true;
          for (let x = x0; x <= x1; x += STEP) {
            const y =
              baseY +
              Math.sin(x * 0.011 + t + li * 0.5) * amp * 0.45 +
              Math.sin(x * 0.031 - t * 0.7 + li) * amp * 0.3 +
              surgeOffset(x, li, prog, amp);
            if (first) {
              ctx.moveTo(x, y);
              first = false;
            } else ctx.lineTo(x, y);
          }
          const ga = (0.05 + prog * 0.17) * s._env;
          ctx.strokeStyle = "rgba(" + GOLD + "," + ga.toFixed(3) + ")";
          ctx.lineWidth = 1.1;
          ctx.stroke();
        }
      }
    };

    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      t += dt * 0.5;

      // spawn surges at irregular, pseudo-random intervals
      surgeTimer -= dt;
      if (surgeTimer <= 0) {
        spawnSurge();
        if (Math.random() < 0.18) spawnSurge(); // occasional brief double
        surgeTimer = rand(1.3, 3.4);
      }

      draw();
      if (running) rafRef.current = requestAnimationFrame(loop);
    };

    if (reduced) {
      // static premium frame: one settled mid-life surge, no motion
      surges.push({
        x0: w * 0.62,
        sigma: w * 0.08,
        skew: 2,
        amp: 0.85,
        dir: -1,
        born: -1.1,
        life: 3,
        gold: false,
      });
      t = 1.1;
      draw();
    } else {
      rafRef.current = requestAnimationFrame(loop);
    }

    // pause animation when the panel is off-screen
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            if (!running && !reduced) {
              running = true;
              last = performance.now();
              rafRef.current = requestAnimationFrame(loop);
            }
          } else if (!reduced) {
            running = false;
            cancelAnimationFrame(rafRef.current);
          }
        });
      },
      { threshold: 0.02 }
    );
    io.observe(canvas);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      io.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="visual-data-canvas"
      aria-hidden="true"
      className="h-full w-full"
    />
  );
};
